// First-run tour: a small corner card walking through the core loop (drop a
// controller, resize its ring, export). Never modal, always skippable, and
// once dismissed or completed it never returns.

import { useEffect, useRef, useState } from 'react'
import { useStore } from '../state/store'
import { CHANNEL_IDS } from '../core/types'
import { IconCheck, IconClose } from './icons'

const TOUR_KEY = 'ventholes-tour-done'

const STEPS = [
  'Pick a channel and drop a Point controller onto the canvas',
  'Drag its dashed influence ring to resize the effect',
  'Press E and save a file from the export dialog',
]

function markDone() {
  try {
    localStorage.setItem(TOUR_KEY, '1')
  } catch {
    // Storage unavailable: the tour will show again next launch, harmless.
  }
}

export function Onboarding() {
  const [visible, setVisible] = useState(() => {
    try {
      return localStorage.getItem(TOUR_KEY) === null
    } catch {
      return false
    }
  })
  const [step, setStep] = useState(0)
  const doc = useStore((s) => s.doc)
  const overlay = useStore((s) => s.overlay)
  const baseline = useRef<{ controllers: number; radii: string } | null>(null)

  // Step detection against a baseline captured on mount.
  useEffect(() => {
    if (!visible) return
    const counts = CHANNEL_IDS.reduce((n, id) => n + doc.channels[id].controllers.length, 0)
    const radii = CHANNEL_IDS.flatMap((id) => doc.channels[id].controllers.map((c) => c.radius)).join(',')
    if (!baseline.current) {
      baseline.current = { controllers: counts, radii }
      return
    }
    if (step === 0 && counts > baseline.current.controllers) {
      baseline.current = { controllers: counts, radii }
      setStep(1)
    } else if (step === 1 && radii !== baseline.current.radii) {
      setStep(2)
    } else if (step === 2 && overlay === 'export') {
      setStep(3)
      markDone()
    }
  }, [visible, doc, overlay, step])

  if (!visible) return null

  return (
    <div className="onboarding holo-frame" role="note" aria-label="Getting started">
      <div className="onboarding-head">
        <span>First flight</span>
        <button
          className="quiet icon-btn"
          aria-label="Dismiss tour"
          onClick={() => {
            markDone()
            setVisible(false)
          }}
        >
          <IconClose size={12} />
        </button>
      </div>
      {STEPS.map((text, i) => (
        <div key={i} className={i === step ? 'onboarding-step hot' : 'onboarding-step'}>
          {i < step ? <IconCheck size={12} /> : <span className="idx">{i + 1}</span>}
          {text}
        </div>
      ))}
      {step >= 3 && (
        <div className="onboarding-step done">
          <IconCheck size={12} />
          That is the whole loop. Enjoy.
          <button style={{ marginLeft: 'auto' }} onClick={() => setVisible(false)}>
            Close
          </button>
        </div>
      )}
    </div>
  )
}
